import { useCallback, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, SvgIcon, Typography } from '@mui/material'
import { Alert } from 'components'
import { Input } from 'components/Form'
import { toastConfig } from 'config'
import { statusTypeCategories, queryClient as qc } from 'keys'
import { LgSpace, LightButton, TablePageStyles, WrapHeader } from 'styles/GlobalStyle'
import { createStatusTypeFetch, deleteStatusTypeFetch, getStatusTypesFetch, updateStatusTypeFetch } from 'store/middlewares/assessment.crud'
import { ReactComponent as EditIcon } from 'assets/icons/Edit.svg'
import { ReactComponent as TrashIcon } from 'assets/icons/Trash.svg'
import { StuckReasonsStyles } from './Style'

const StuckReasons = () => {
    const queryClient = useQueryClient()

    const [editing, setEditing] = useState(null) // null=closed, {}=new, {id,label}=edit
    const [deleting, setDeleting] = useState(null)
    const [labelValue, setLabelValue] = useState("")

    const statusTypes = useQuery({
        queryKey: [qc.statusTypes, statusTypeCategories.stuck],
        queryFn: async () => {
            const { data } = await getStatusTypesFetch(statusTypeCategories.stuck)
            return data
        }
    })

    const invalidate = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: [qc.statusTypes] })
    }, [])

    const closeForm = useCallback(() => {
        setEditing(null)
        setLabelValue("")
    }, [])

    const { mutateAsync: createStatusType, isPending: createLoading } = useMutation({
        mutationFn: createStatusTypeFetch,
        onSuccess: () => {
            invalidate()
            closeForm()
        },
        onError: () => toast.error("שגיאה בשמירה, נסי שוב", toastConfig)
    })

    const { mutateAsync: updateStatusType, isPending: updateLoading } = useMutation({
        mutationFn: updateStatusTypeFetch,
        onSuccess: () => {
            invalidate()
            closeForm()
        },
        onError: () => toast.error("שגיאה בשמירה, נסי שוב", toastConfig)
    })

    const { mutateAsync: deleteStatusType, isPending: deleteLoading } = useMutation({
        mutationFn: deleteStatusTypeFetch,
        onSuccess: () => {
            invalidate()
            setDeleting(null)
        },
        onError: (error) => {
            toast.error(error?.response?.data?.detail || "לא ניתן למחוק סיבה זו", toastConfig)
            setDeleting(null)
        }
    })

    const openAdd = useCallback(() => {
        setLabelValue("")
        setEditing({})
    }, [])

    const openEdit = useCallback((row) => {
        setLabelValue(row.label)
        setEditing(row)
    }, [])

    const saveForm = useCallback(async () => {
        if (!labelValue?.trim())
            return

        if (editing?.id)
            await updateStatusType({ id: editing.id, label: labelValue })
        else
            await createStatusType({ label: labelValue, category: statusTypeCategories.stuck })
    }, [editing, labelValue])

    const isSaving = createLoading || updateLoading

    return (
        <StuckReasonsStyles>
            <WrapHeader height={50}>
                <div className='header'>
                    <Typography variant="h6">סיבות תקיעות</Typography>
                    <LightButton variant="solid" size="small" color="thirdly" label="הוסף סיבה" action={openAdd} />
                </div>
            </WrapHeader>

            <LgSpace />

            <TablePageStyles hideSpace>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>סיבה</TableCell>
                                <TableCell align="left">פעולות</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {statusTypes.data?.map(row => (
                                <TableRow key={row.id}>
                                    <TableCell>{row.label}</TableCell>
                                    <TableCell align="left">
                                        <IconButton onClick={() => openEdit(row)}>
                                            <SvgIcon component={EditIcon} inheritViewBox />
                                        </IconButton>
                                        <IconButton onClick={() => setDeleting(row)}>
                                            <SvgIcon component={TrashIcon} inheritViewBox />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </TablePageStyles>

            {editing &&
                <Alert
                    systemUI
                    title={editing?.id ? "עריכת סיבה" : "הוספת סיבה חדשה"}
                    handleAlert={closeForm}
                    footerActions={[
                        { label: "ביטול", color: "thirdly4", action: closeForm },
                        { label: "שמירה", color: "thirdly", action: saveForm, loading: isSaving, disabled: isSaving || !labelValue?.trim() }
                    ]}
                >
                    <Input
                        label="סיבה"
                        placeholder="לדוגמה: אודיו לא תקין"
                        input={{ value: labelValue, onChange: setLabelValue }}
                    />
                </Alert>
            }

            {deleting &&
                <Alert
                    systemUI
                    title="מחיקת סיבה"
                    handleAlert={() => setDeleting(null)}
                    footerActions={[
                        { label: "ביטול", color: "thirdly4", action: () => setDeleting(null) },
                        { label: "מחיקה", variant: "danger", action: () => deleteStatusType({ id: deleting.id }), loading: deleteLoading, disabled: deleteLoading }
                    ]}
                >
                    {`למחוק את הסיבה "${deleting.label}"? פעולה זו לא ניתנת לביטול.`}
                </Alert>
            }
        </StuckReasonsStyles>
    )
}

export default StuckReasons
